import React, { useState, useEffect } from 'react';
import { DollarSign, Calendar, Clock, Plus, Trash2, Edit3, MessageCircle, Share2, Link2, Star, ThumbsUp, Archive, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ProfileEditModal from '../shared/ProfileModal';
import VideoCard from '../../UI/VideoCard';
import WorkDetailModal, { WorkDetail } from '../work/WorkDetailModal';
import SimpleUploadModal from '../shared/SimpleUploadModal';
import WorkSelectorModal from '../work/WorkSelectorModal';
import ExampleWorkSourceModal from '../shared/ExampleWorkSourceModal';
import ExampleWorkUrlModal from '../shared/ExampleWorkUrlModal';
import ExampleWorkEditModal from '../shared/ExampleWorkEditModal';
import ExampleWorkViewModal from '../shared/ExampleWorkViewModal';
import ExampleWorkUrlCard from '../shared/ExampleWorkUrlCard';
import { ALL_LEAF_CATEGORIES, STYLE_FACETS } from '../../../constants/tags';
import { SelectTags, type NewTagState } from '../../Tags/SelectTags';
import { deriveTagStateFromSource, flattenTagStateToNames, isKnownToolTag } from '../../Tags/tagStateUtils';
import { uploadMedia } from '../../../services/mediaUploadService';
import { useConfirm, useToast } from '../../UI/Toast';
import { pendingExampleWorksService, type PendingExampleWork } from '../../../services/pendingExampleWorksService';
import { postService, type WorkSummary } from '../../../services/postService';
import {
    buildServiceExampleWorkThreadId,
    mapWorkSummaryToExampleWork,
    normalizePersistedExampleWorkUrl,
    type ExampleWorkValue
} from '../shared/exampleWorkHelpers';
import { buildServiceSharePath, buildServiceShareText, buildServiceShareUrl, buildShareClipboardText, copyTextToClipboard } from '../../../utils/share';

// Reuse types or define new ones
export interface ServiceReview {
    id: number;
    avatar: string;
    username: string;
    rating: number;
    content: string;
    createdAt: string;
    helpfulCount: number;
}

export interface ServiceDetailType {
    id: string | number;
    title: string;
    description: string;
    priceRange: string;
    deliveryTimeRange: string;
    contentDurationRange?: string; // Optional, for video services
    status?: 'draft' | 'active' | 'archived';
    displayOrder?: number;
    tags: string[];
    // Phase 1: structured tag fields (round-trip with backend)
    primaryCategory?: string | null;
    secondaryCategories?: string[];
    styleFacets?: string[];
    toolTags?: string[];
    exampleWorks: {
        id: number | string;
        type: 'video' | 'image';
        thumbnailUrl: string;
        videoUrl?: string;
        images?: string[];
        title: string;
        description?: string;
        sourceUrl?: string;
    }[];
    reviews: ServiceReview[];
}

interface ServiceDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    service?: ServiceDetailType;
    isOwnProfile?: boolean;
    onSave?: (updatedService: ServiceDetailType) => void;
    initialIsEditing?: boolean;
    userInfo?: { id: string; name: string; avatar: string };
}

type ExampleWorkDraft = ServiceDetailType['exampleWorks'][number] & {
    localFile?: File;
    localThumbnailDataUrl?: string;
};

type ExampleWorkPreview = ExampleWorkDraft & {
    commentThreadId?: string;
    comments?: WorkDetail['comments'];
    commentCount?: number;
};

type ServiceFormData = Omit<ServiceDetailType, 'exampleWorks'> & {
    exampleWorks: ExampleWorkDraft[];
};


const emptyService: ServiceDetailType = {
    id: 0,
    title: '',
    description: '',
    priceRange: '',
    deliveryTimeRange: '',
    status: 'draft',
    tags: [],
    exampleWorks: [],
    reviews: []
};

const normalizeService = (input?: ServiceDetailType): ServiceDetailType => {
    if (!input) {
        return { ...emptyService };
    }

    return {
        ...emptyService,
        ...input,
        tags: Array.isArray(input.tags) ? input.tags : [],
        exampleWorks: Array.isArray(input.exampleWorks) ? input.exampleWorks : [],
        reviews: Array.isArray(input.reviews) ? input.reviews : []
    };
};

const ServiceDetailModal: React.FC<ServiceDetailModalProps> = ({
    isOpen,
    onClose,
    service: initialService,
    isOwnProfile = false,
    onSave,
    initialIsEditing = false,
    userInfo
}) => {

    const [service, setService] = useState<ServiceDetailType>(() => normalizeService(initialService));
    const [formData, setFormData] = useState<ServiceFormData>(() => normalizeService(initialService) as ServiceFormData);

    // NEW Phase 1: Layered tag state
    const [tagState, setTagState] = useState<NewTagState>({
        primaryCategory: null,
        secondaryCategories: [],
        toolTags: [],
        styleFacets: [],
    });

    const setFormField = <K extends keyof ServiceFormData>(key: K, value: ServiceFormData[K]) => {
        setFormData(prev => ({ ...prev, [key]: value }));
        setHasUnsavedChanges(true);
    };

    const isLocalUrl = (url?: string) => !!url && (url.startsWith('blob:') || url.startsWith('data:'));

    const stripLocalFields = (work: ExampleWorkDraft): ServiceDetailType['exampleWorks'][number] => {
        const { localFile, localThumbnailDataUrl, ...rest } = work;
        return rest;
    };

    const normalizePersistedUrl = (url?: string) => {
        return normalizePersistedExampleWorkUrl(url);
    };

    const uploadExampleWorks = async (works: ExampleWorkDraft[]) => {
        const uploaded: ServiceDetailType['exampleWorks'] = [];

        for (const work of works) {
            if (work.type === 'image') {
                let fileUrl = work.thumbnailUrl;
                let thumbnailUrl = work.thumbnailUrl;
                if (work.localFile) {
                    const uploadResult = await uploadMedia(work.localFile, undefined, () => undefined, {
                        altText: work.title
                    });
                    if (!uploadResult?.fileUrl) {
                        throw new Error('示例作品上传失败');
                    }
                    fileUrl = uploadResult.fileUrl;
                    thumbnailUrl = uploadResult.thumbnailUrl || uploadResult.fileUrl;
                }

                const resolvedUrl = normalizePersistedUrl(fileUrl || work.thumbnailUrl);
                const resolvedThumbnailUrl = normalizePersistedUrl(thumbnailUrl || resolvedUrl);
                const normalizedImages = work.images && work.images.length > 0
                    ? work.images.map((img) => {
                        if (isLocalUrl(img) && resolvedUrl && !isLocalUrl(resolvedUrl)) {
                            return resolvedUrl;
                        }
                        return normalizePersistedUrl(img);
                    })
                    : (resolvedUrl && !isLocalUrl(resolvedUrl) ? [resolvedUrl] : (work.images || []).map(normalizePersistedUrl));

                uploaded.push({
                    ...stripLocalFields(work),
                    thumbnailUrl: resolvedThumbnailUrl,
                    images: normalizedImages.filter((img) => img !== '')
                });
                continue;
            }

            let videoUrl = work.videoUrl;
            let thumbUrl = work.thumbnailUrl;
            if (work.localFile) {
                const uploadResult = await uploadMedia(work.localFile, undefined, () => undefined, {
                    altText: work.title
                });
                if (!uploadResult?.fileUrl) {
                    throw new Error('示例作品上传失败');
                }
                videoUrl = uploadResult.fileUrl;
                thumbUrl = uploadResult.thumbnailUrl || work.thumbnailUrl;
            }
            videoUrl = normalizePersistedUrl(videoUrl);
            thumbUrl = normalizePersistedUrl(thumbUrl);

            uploaded.push({
                ...stripLocalFields(work),
                videoUrl,
                thumbnailUrl: thumbUrl
            });
        }

        return uploaded;
    };

    const cloneExampleWorksSnapshot = (works: ExampleWorkDraft[]) => works.map((work) => ({
        ...work,
        images: work.images ? [...work.images] : undefined
    }));

    const getPersistedExampleWorks = (works: ExampleWorkDraft[]): ServiceDetailType['exampleWorks'] => {
        const persistedWorks: ServiceDetailType['exampleWorks'] = [];

        works.forEach((work) => {
            if (work.localFile) {
                return;
            }

            if (work.sourceUrl) {
                persistedWorks.push({
                    ...stripLocalFields(work),
                    thumbnailUrl: normalizePersistedUrl(work.thumbnailUrl) || '',
                    videoUrl: normalizePersistedUrl(work.videoUrl) || undefined,
                    images: (work.images || []).map(normalizePersistedUrl).filter(Boolean)
                });
                return;
            }

            if (work.type === 'image') {
                const normalizedImages = (work.images || [])
                    .map(normalizePersistedUrl)
                    .filter((url) => url && !isLocalUrl(url));
                const normalizedThumbnailUrl = normalizePersistedUrl(work.thumbnailUrl);
                const resolvedImages = normalizedImages.length > 0
                    ? normalizedImages
                    : (normalizedThumbnailUrl && !isLocalUrl(normalizedThumbnailUrl) ? [normalizedThumbnailUrl] : []);
                const resolvedThumbnailUrl = normalizedThumbnailUrl || resolvedImages[0] || '';

                if (!resolvedThumbnailUrl && resolvedImages.length === 0) {
                    return;
                }

                persistedWorks.push({
                    ...stripLocalFields(work),
                    thumbnailUrl: resolvedThumbnailUrl,
                    images: resolvedImages
                });
                return;
            }

            const normalizedVideoUrl = normalizePersistedUrl(work.videoUrl);
            const normalizedThumbnailUrl = normalizePersistedUrl(work.thumbnailUrl);
            if (!normalizedVideoUrl && !normalizedThumbnailUrl) {
                return;
            }

            persistedWorks.push({
                ...stripLocalFields(work),
                videoUrl: normalizedVideoUrl || undefined,
                thumbnailUrl: normalizedThumbnailUrl || ''
            });
        });

        return persistedWorks;
    };

    const getPendingExampleWorkPlaceholders = (works: ExampleWorkDraft[]): PendingExampleWork[] => {
        return works
            .filter((work) => work.localFile instanceof File)
            .map((work) => ({
                id: work.id,
                type: work.type,
                title: work.title,
                thumbnailUrl: work.localThumbnailDataUrl || work.thumbnailUrl,
                videoUrl: work.videoUrl,
                images: work.images,
                uploadState: 'uploading'
            }));
    };

    const syncExampleWorksInBackground = (serviceId: string, worksSnapshot: ExampleWorkDraft[]) => {
        if (!worksSnapshot.some((work) => work.localFile instanceof File)) {
            return;
        }

        void (async () => {
            try {
                const uploadedWorks = await uploadExampleWorks(worksSnapshot);
                const { serviceService } = await import('../../../services/serviceService');
                const res = await serviceService.updateService(serviceId, { exampleWorks: uploadedWorks });

                if (!res.success || !res.data) {
                    throw new Error(res.error || '示例作品上传失败');
                }

                const nextService = normalizeService(((res.data as any).data ?? res.data) as ServiceDetailType);
                if (isMountedRef.current) {
                    setService(nextService);
                    if (!hasUnsavedChangesRef.current) {
                        setFormData((prev) => ({
                            ...prev,
                            id: nextService.id,
                            status: nextService.status,
                            exampleWorks: uploadedWorks as ExampleWorkDraft[]
                        }));
                    }
                }

                pendingExampleWorksService.clearPendingExampleWorks('service', serviceId);
                window.dispatchEvent(new CustomEvent('services_updated'));
                showToast('服务示例作品上传完成', 'success');
            } catch (error) {
                console.error('Background service example works upload failed:', error);
                pendingExampleWorksService.clearPendingExampleWorks('service', serviceId);
                showToast('服务示例作品上传失败，请重新编辑后重试', 'error');
            }
        })();
    };

    // UI States
    const [isEditing, setIsEditing] = useState(initialIsEditing);
    const [activeTab, setActiveTab] = useState<'works' | 'reviews'>('works');
    const [selectedWork, setSelectedWork] = useState<WorkDetail | null>(null);
    const [selectedUrlWork, setSelectedUrlWork] = useState<ExampleWorkPreview | null>(null);
    const [editingExampleWork, setEditingExampleWork] = useState<ExampleWorkDraft | null>(null);
    const [isShareCopied, setIsShareCopied] = useState(false);
    const [isResolvingShare, setIsResolvingShare] = useState(false);
    const [isResolvingChat, setIsResolvingChat] = useState(false);
    const [submitMode, setSubmitMode] = useState<'draft' | 'publish' | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const isSubmitting = submitMode !== null;
    const navigate = useNavigate();
    const currentUser = userInfo || { id: '0', name: '', avatar: '' };
    const { confirm } = useConfirm();
    const { showToast } = useToast();

    // Reset local state when isOpen/initialService changes
    // Sub-modals state
    const [showExampleWorkSourceModal, setShowExampleWorkSourceModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showWorkSelectorModal, setShowWorkSelectorModal] = useState(false);
    const [showUrlModal, setShowUrlModal] = useState(false);
    const [availableWorks, setAvailableWorks] = useState<WorkSummary[]>([]);
    const [isLoadingAvailableWorks, setIsLoadingAvailableWorks] = useState(false);

    const initializedRef = React.useRef(false);
    const activeServiceIdRef = React.useRef<string | number | undefined>(undefined);
    const isMountedRef = React.useRef(true);
    const hasUnsavedChangesRef = React.useRef(false);
    const [pendingExampleWorks, setPendingExampleWorks] = useState<PendingExampleWork[]>([]);

    useEffect(() => {
        hasUnsavedChangesRef.current = hasUnsavedChanges;
    }, [hasUnsavedChanges]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!isOpen) {
            initializedRef.current = false;
            activeServiceIdRef.current = undefined;
            return;
        }

        const incomingId = initialService?.id;
        const isSameService = initializedRef.current && incomingId === activeServiceIdRef.current;
        if (isSameService) return;

        const dataToUse = normalizeService(initialService);

        setService(dataToUse);
        setFormData(dataToUse as ServiceFormData);
        setTagState(deriveTagStateFromSource(dataToUse));
        setIsEditing(initialIsEditing || !initialService);
        setHasUnsavedChanges(false);
        setActiveTab('works');
        initializedRef.current = true;
        activeServiceIdRef.current = incomingId;
    }, [isOpen, initialService, initialIsEditing]);

    useEffect(() => {
        const currentId = service.id || initialService?.id;
        setPendingExampleWorks(pendingExampleWorksService.getPendingExampleWorks('service', currentId));

        if (!isOpen || !currentId) {
            return;
        }

        const handlePendingExampleWorksChanged = (event: Event) => {
            const detail = (event as CustomEvent<{ entityType: 'service' | 'brief'; entityId: string }>).detail;
            if (!detail || detail.entityType !== 'service' || detail.entityId !== String(currentId)) {
                return;
            }

            setPendingExampleWorks(pendingExampleWorksService.getPendingExampleWorks('service', currentId));
        };

        window.addEventListener(pendingExampleWorksService.eventName, handlePendingExampleWorksChanged);
        return () => window.removeEventListener(pendingExampleWorksService.eventName, handlePendingExampleWorksChanged);
    }, [initialService?.id, isOpen, service.id]);

    // Review Stats
    const serviceExampleWorks = service.exampleWorks || [];
    const serviceReviews = service.reviews || [];
    const formExampleWorks = formData.exampleWorks || [];
    const serviceDisplayTags = flattenTagStateToNames(deriveTagStateFromSource(service));

    const totalReviews = serviceReviews.length;
    const positiveReviews = serviceReviews.filter(r => r.rating >= 4).length;
    const averageRating = totalReviews > 0
        ? (serviceReviews.reduce((acc, curr) => acc + curr.rating, 0) / totalReviews).toFixed(1)
        : '0.0';

    const handleSave = async (mode: 'draft' | 'publish', e?: React.FormEvent, exitAfterSave = false) => {
        e?.preventDefault();
        setSubmitMode(mode);
        let saveSucceeded = false;
        let savedService: ServiceDetailType | null = null;

        try {
            const alTags = flattenTagStateToNames(tagState);
            const exampleWorksSnapshot = cloneExampleWorksSnapshot(formExampleWorks);
            const hasPendingExampleUploads = exampleWorksSnapshot.some((work) => work.localFile instanceof File);
            const exampleWorks = getPersistedExampleWorks(exampleWorksSnapshot);
            const pendingExamplePlaceholders = getPendingExampleWorkPlaceholders(exampleWorksSnapshot);

            const nextStatus: ServiceDetailType['status'] = mode === 'draft' ? 'draft' : 'active';
            const updatedData: ServiceDetailType = {
                ...formData,
                tags: alTags,
                primaryCategory: tagState.primaryCategory,
                secondaryCategories: tagState.secondaryCategories,
                styleFacets: tagState.styleFacets,
                toolTags: tagState.toolTags,
                exampleWorks,
                status: nextStatus
            };

            const { serviceService } = await import('../../../services/serviceService');

            if (isCreateMode) {
                const { id, reviews, ...createData } = updatedData as any;
                const res = await serviceService.createService(createData);
                if (res.success && res.data) {
                    savedService = normalizeService(((res.data as any).data ?? res.data) as ServiceDetailType);
                    setService(savedService);
                    if (hasPendingExampleUploads && mode === 'draft' && !exitAfterSave) {
                        setFormData((prev) => ({
                            ...prev,
                            id: savedService!.id,
                            status: savedService!.status
                        }));
                    } else {
                        setFormData(savedService as ServiceFormData);
                    }
                    window.dispatchEvent(new CustomEvent('services_updated'));
                    if (mode === 'publish') {
                        onSave?.(savedService);
                        if (isOwnProfile) {
                            window.dispatchEvent(new CustomEvent('change_profile_tab', { detail: { tab: 'services' } }));
                        }
                    }
                    if (hasPendingExampleUploads) {
                        pendingExampleWorksService.setPendingExampleWorks('service', savedService.id, pendingExamplePlaceholders);
                        syncExampleWorksInBackground(String(savedService.id), exampleWorksSnapshot);
                    }
                    saveSucceeded = true;
                } else {
                    console.error('Failed to create service', res.error);
                }
            } else {
                const res = await serviceService.updateService(updatedData.id as string, updatedData);
                if (res.success && res.data) {
                    savedService = normalizeService(((res.data as any).data ?? res.data) as ServiceDetailType);
                    setService(savedService);
                    if (hasPendingExampleUploads && mode === 'draft' && !exitAfterSave) {
                        setFormData((prev) => ({
                            ...prev,
                            id: savedService!.id,
                            status: savedService!.status
                        }));
                    } else {
                        setFormData(savedService as ServiceFormData);
                    }
                    window.dispatchEvent(new CustomEvent('services_updated'));
                    if (mode === 'publish') {
                        onSave?.(savedService);
                    }
                    if (hasPendingExampleUploads) {
                        pendingExampleWorksService.setPendingExampleWorks('service', savedService.id, pendingExamplePlaceholders);
                        syncExampleWorksInBackground(String(savedService.id), exampleWorksSnapshot);
                    }
                    saveSucceeded = true;
                } else {
                    console.error('Failed to update service', res.error);
                }
            }
        } catch (error) {
            console.error('Error saving service:', error);
        } finally {
            setSubmitMode(null);

            if (saveSucceeded) {
                setHasUnsavedChanges(false);
                if (mode === 'publish') {
                    if (isCreateMode || service.id === '' || service.id === 0) {
                        onClose();
                    } else {
                        setIsEditing(false);
                    }
                } else if (exitAfterSave) {
                    if (savedService) {
                        setFormData(savedService as ServiceFormData);
                    }
                    if (isCreateMode || !savedService) {
                        onClose();
                    } else {
                        setIsEditing(false);
                    }
                }
            }
        }
    };

    const closeEditor = () => {
        if (isCreateMode) {
            onClose();
            return;
        }

        setFormData(service as ServiceFormData);
        setTagState(deriveTagStateFromSource(service));
        setIsEditing(false);
        setHasUnsavedChanges(false);
    };

    const handleRequestClose = async () => {
        if (isSubmitting) {
            return;
        }

        if (!isEditing) {
            onClose();
            return;
        }

        if (!hasUnsavedChanges) {
            closeEditor();
            return;
        }

        const result = await confirm({
            title: '保存草稿并退出？',
            message: '当前有未保存的修改，是否先保存为草稿再退出？',
            confirmText: '保存并退出',
            cancelText: '直接退出',
            dismissible: false,
            showCloseButton: true
        });

        if (result.action === 'confirm') {
            await handleSave('draft', undefined, true);
            return;
        }

        if (result.action === 'cancel') {
            closeEditor();
        }
    };

    const handleRemoveWork = (id: number | string) => {
        setFormData(prev => ({
            ...prev,
            exampleWorks: prev.exampleWorks.filter(w => w.id !== id)
        }));
        setHasUnsavedChanges(true);
    };

    const appendExampleWorks = (works: ExampleWorkValue[]) => {
        if (works.length === 0) {
            return;
        }

        setFormData((prev) => {
            const existingIds = new Set(prev.exampleWorks.map((work) => String(work.id)));
            const nextWorks = works.filter((work) => !existingIds.has(String(work.id)));

            if (nextWorks.length === 0) {
                return prev;
            }

            return {
                ...prev,
                exampleWorks: [...prev.exampleWorks, ...(nextWorks as ExampleWorkDraft[])]
            };
        });
        setHasUnsavedChanges(true);
    };

    const handlePublishWork = (data: { type: 'video' | 'image', file: File, previewUrl: string, thumbnailUrl: string, title?: string, description?: string }) => {
        const newWork: ExampleWorkDraft = {
            id: Date.now(),
            type: data.type,
            thumbnailUrl: data.thumbnailUrl,
            title: data.title || "未命名作品",
            description: data.description || '',
            videoUrl: data.type === 'video' ? data.previewUrl : undefined,
            images: data.type === 'image' ? [data.previewUrl] : [],
            localFile: data.file,
            localThumbnailDataUrl: data.type === 'video' ? data.thumbnailUrl : undefined
        };
        appendExampleWorks([newWork]);
    };

    const handleOpenCreatorWorks = async () => {
        if (!currentUser.id || currentUser.id === '0') {
            showToast('暂时无法获取你的作品列表', 'error');
            return;
        }

        setShowExampleWorkSourceModal(false);
        setAvailableWorks([]);
        setShowWorkSelectorModal(true);
        setIsLoadingAvailableWorks(true);
        try {
            const works = await postService.getCreatorPosts(currentUser.id, 100);
            setAvailableWorks(works.filter((work) => Boolean(work.thumbnailUrl || work.videoUrl || work.images?.length)));
        } catch (error) {
            console.error('Failed to load creator works for example selection:', error);
            showToast('加载作品列表失败，请稍后重试', 'error');
        } finally {
            setIsLoadingAvailableWorks(false);
        }
    };

    const handleSelectExampleWorkSource = (source: 'upload' | 'myWorks' | 'url') => {
        if (source === 'upload') {
            setShowExampleWorkSourceModal(false);
            setShowUploadModal(true);
            return;
        }

        if (source === 'myWorks') {
            void handleOpenCreatorWorks();
            return;
        }

        setShowExampleWorkSourceModal(false);
        setShowUrlModal(true);
    };

    const handleSelectCreatorWorks = (works: WorkSummary[]) => {
        appendExampleWorks(works.map(mapWorkSummaryToExampleWork));
    };

    const handleImportUrlWork = (work: ExampleWorkValue) => {
        appendExampleWorks([work]);
    };

    const handleStartEditExampleWork = (work: ExampleWorkDraft) => {
        setEditingExampleWork(work);
    };

    const handleSaveEditedExampleWork = (patch: Partial<ExampleWorkDraft> & { title: string; description?: string }) => {
        if (!editingExampleWork) {
            return;
        }

        setFormData((prev) => ({
            ...prev,
            exampleWorks: prev.exampleWorks.map((work) => (
                String(work.id) === String(editingExampleWork.id)
                    ? { ...work, ...patch }
                    : work
            ))
        }));
        setHasUnsavedChanges(true);
        setEditingExampleWork(null);
    };

    const handleWorkClick = (work: any) => {
        if (isEditing) return;
        const commentThreadId = service.id
            ? buildServiceExampleWorkThreadId(service.id, work.id)
            : undefined;
        if (work.sourceUrl) {
            setSelectedUrlWork({
                ...work,
                commentThreadId,
                ownerUserId: userInfo?.id || currentUser.id,
                comments: [],
                commentCount: 0
            });
            return;
        }
        const fullWork: WorkDetail = {
            id: work.id,
            type: work.type,
            title: work.title,
            thumbnailUrl: work.thumbnailUrl,
            videoUrl: work.videoUrl,
            images: work.images,
            description: work.description || "这是服务案例作品展示。",
            sourceUrl: work.sourceUrl,
            tags: [],
            likes: 0,
            views: 0,
            favorites: 0,
            comments: [],
            commentThreadId,
            createdAt: new Date().toISOString(),
            author: currentUser
        };
        setSelectedWork(fullWork);
    };

    const isCreateMode = !service.id || service.id === 0;
    const displayedExampleWorks = isEditing ? formExampleWorks : [...serviceExampleWorks, ...pendingExampleWorks];

    const resolveServiceOwner = async () => {
        let ownerId = userInfo?.id;
        let ownerName = userInfo?.name;
        let ownerAvatar = userInfo?.avatar;

        if (!ownerId) {
            const { serviceService } = await import('../../../services/serviceService');
            const response = await serviceService.getServiceById(service.id);
            const payload = response.data as any;
            const detail = payload?.data ?? payload;
            ownerId = detail?.creator?.userId;
            ownerName = detail?.creator?.displayName || detail?.creator?.username || ownerId;
            ownerAvatar = detail?.creator?.avatarUrl;
        }

        if (!ownerId) {
            throw new Error('暂时无法获取服务发布者信息');
        }

        return {
            ownerId,
            ownerName: ownerName || ownerId,
            ownerAvatar: ownerAvatar || ''
        };
    };

    const handleArchiveToggle = async () => {
        if (!service.id) return;
        const isArchived = service.status === 'archived';
        const result = await confirm({
            title: isArchived ? '上架服务' : '下架服务',
            message: isArchived
                ? '重新上架后，服务会回到可展示列表。'
                : '下架后服务不会删除，会在你的列表底部以灰色水印显示。',
            confirmText: isArchived ? '上架' : '下架',
            cancelText: '取消',
            tone: isArchived ? 'default' : 'danger'
        });
        if (result.action !== 'confirm') return;

        try {
            const { serviceService } = await import('../../../services/serviceService');
            const res = isArchived
                ? await serviceService.restoreService(service.id as string)
                : await serviceService.archiveService(service.id as string);
            const nextService = ((res.data as any)?.data ?? res.data) as ServiceDetailType | undefined;
            if (res.success && nextService) {
                const normalized = normalizeService(nextService);
                setService(normalized);
                setFormData(normalized as ServiceFormData);
                window.dispatchEvent(new CustomEvent('services_updated'));
                showToast(isArchived ? '服务已上架' : '服务已下架', 'success');
            } else {
                showToast(`${isArchived ? '上架' : '下架'}失败：${res.error || '未知错误'}`, 'error');
            }
        } catch (error) {
            console.error('Archive service error:', error);
            showToast('操作失败，请稍后重试', 'error');
        }
    };

    const handleDelete = async () => {
        if (!service.id) return;
        const result = await confirm({
            title: '删除服务',
            message: '删除后该服务会从列表中移除，无法通过上架恢复。确定继续吗？',
            confirmText: '删除',
            cancelText: '取消',
            tone: 'danger'
        });
        if (result.action !== 'confirm') return;

        try {
            const { serviceService } = await import('../../../services/serviceService');
            const res = await serviceService.deleteService(service.id as string);
            if (res.success) {
                window.dispatchEvent(new CustomEvent('services_updated'));
                showToast('服务已删除', 'success');
                onClose();
            } else {
                showToast(`删除失败：${res.error || '未知错误'}`, 'error');
            }
        } catch (error) {
            console.error('Delete service error:', error);
            showToast('删除失败，请稍后重试', 'error');
        }
    };

    const handleOpenShare = async () => {
        if (!service.id || isResolvingShare) {
            return;
        }

        setIsResolvingShare(true);
        try {
            const { ownerId, ownerName } = await resolveServiceOwner();
            const shareText = buildServiceShareText(service.title, ownerName || '这位创作者');
            const shareUrl = buildServiceShareUrl(ownerId, service.id);
            await copyTextToClipboard(buildShareClipboardText(shareText, shareUrl));
            setIsShareCopied(true);
            window.setTimeout(() => setIsShareCopied(false), 1800);
            showToast('分享信息已复制', 'success');
        } catch (error: any) {
            console.error('Prepare service share failed:', error);
            showToast(error?.message || '分享信息复制失败', 'error');
        } finally {
            setIsResolvingShare(false);
        }
    };

    const headerActions = (
        <>
            {!isEditing && !!service.id && (
                <button
                    onClick={() => void handleOpenShare()}
                    disabled={isResolvingShare}
                    className="p-2 rounded-full font-medium text-sm flex items-center justify-center transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800 disabled:opacity-60"
                    title="分享服务"
                >
                    {isShareCopied ? <Link2 className="w-4 h-4 text-blue-600" /> : <Share2 className="w-4 h-4" />}
                </button>
            )}
            {!isEditing && isOwnProfile && (
                <>
                    <button
                        onClick={handleDelete}
                        className="p-2 rounded-full font-medium text-sm flex items-center justify-center transition-colors bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-600"
                        title="删除服务"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={handleArchiveToggle}
                        className={`px-4 py-2 rounded-full font-medium text-sm flex items-center gap-2 transition-colors ${service.status === 'archived'
                            ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        title={service.status === 'archived' ? '上架服务' : '下架服务'}
                    >
                        {service.status === 'archived'
                            ? <RotateCcw className="w-4 h-4" />
                            : <Archive className="w-4 h-4" />}
                        {service.status === 'archived' ? '上架' : '下架'}
                    </button>
                    <button
                        onClick={() => {
                            setIsEditing(true);
                            setHasUnsavedChanges(false);
                        }}
                        className="px-4 py-2 rounded-full font-medium text-sm flex items-center gap-2 transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                        <Edit3 className="w-4 h-4" />
                        编辑
                    </button>
                </>
            )}

            {!isEditing && !isOwnProfile && service.status !== 'archived' && (
                <button
                    onClick={() => void (async () => {
                        if (isResolvingChat) return;

                        setIsResolvingChat(true);
                        try {
                            const { ownerId } = await resolveServiceOwner();
                            const convId = `C2C${ownerId}`;
                            navigate(`/messages/${convId}`, {
                                state: {
                                    returnTo: buildServiceSharePath(ownerId, service.id),
                                    category: 'SERVICE',
                                    referenceId: service.id.toString(),
                                    referenceTitle: service.title,
                                    referenceDetails: {
                                        title: service.title,
                                        description: service.description,
                                        priceRange: service.priceRange,
                                        deliveryTimeRange: service.deliveryTimeRange,
                                        tags: service.tags,
                                    },
                                }
                            });
                        } catch (error: any) {
                            console.error('Create service chat thread failed:', error);
                            showToast(error?.message || '暂时无法发起会话', 'error');
                        } finally {
                            setIsResolvingChat(false);
                        }
                    })()}
                    disabled={isResolvingChat}
                    className="px-4 py-2 rounded-full font-medium text-sm flex items-center gap-2 transition-colors bg-black text-white hover:bg-gray-800 disabled:opacity-60"
                >
                    <MessageCircle className="w-4 h-4" />
                    聊一聊
                </button>
            )}
        </>
    );

    return (
        <ProfileEditModal
            isOpen={isOpen}
            onClose={() => void handleRequestClose()}
            title={isEditing
                ? (isCreateMode ? "发布服务" : "编辑服务")
                : "服务详情"
            }
            description={isEditing
                ? (isCreateMode ? "发布您的专业技能，为客户提供定制化服务" : "修改服务详情和展示信息")
                : undefined
            }
            headerActions={headerActions}
            enableFullscreen={isEditing}
            zIndex={60}
        >
            <form onSubmit={(e) => e.preventDefault()} className="flex flex-col h-full">
                {/* Scrollable Content */}
                <div className="flex-1 min-h-0">
                    {/* Top Area: Service Info */}
                    <div className={`space-y-8 pb-8 ${isEditing ? 'bg-white p-8 rounded-2xl shadow-sm border border-gray-100 mb-8' : 'border-b border-gray-100 bg-gray-50/50 p-8'}`}>
                        <div className="max-w-4xl mx-auto">
                            {/* Title & Tags */}
                            <div className="mb-6 space-y-4">
                                {isEditing ? (
                                    <>
                                        <div className="space-y-2">
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider">服务标题 <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                value={formData.title}
                                                onChange={e => setFormField('title', e.target.value)}
                                                className="w-full text-3xl font-bold p-0 border-none focus:ring-0 placeholder-gray-300 text-gray-900 bg-transparent"
                                                placeholder="请输入服务标题"
                                                required
                                            />
                                            <div className="h-px w-full bg-gray-100"></div>
                                        </div>

                                        {/* NEW Phase 1: Layered Tags */}
                                        <div className="pt-2">
                                            <SelectTags
                                                value={tagState}
                                                onChange={(next) => {
                                                    setTagState(next);
                                                    setHasUnsavedChanges(true);
                                                }}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <h2 className="text-3xl font-bold text-gray-900 mb-4 leading-tight flex items-center gap-2">
                                            {service.title}
                                            {service.status === 'draft' && (
                                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                                    草稿
                                                </span>
                                            )}
                                            {service.status === 'archived' && (
                                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-500">
                                                    已下架
                                                </span>
                                            )}
                                        </h2>
                                    </>
                                )}
                            </div>

                            {/* Description */}
                            <div className="mb-8">
                                {isEditing ? (
                                    <div className="space-y-2">
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider">服务详情描述</label>
                                        <textarea
                                            value={formData.description}
                                            onChange={e => {
                                                setFormField('description', e.target.value);
                                                e.target.style.height = 'auto';
                                                e.target.style.height = e.target.scrollHeight + 'px';
                                            }}
                                            ref={(el) => {
                                                if (el) {
                                                    el.style.height = 'auto';
                                                    el.style.height = el.scrollHeight + 'px';
                                                }
                                            }}
                                            rows={4}
                                            className="w-full p-4 border-none bg-gray-50 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 text-base leading-relaxed text-gray-700 resize-vertical transition-all min-h-[160px]"
                                            placeholder="详细描述您提供的服务内容、流程以及优势..."
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <p className="text-gray-600 text-base leading-relaxed mb-6 whitespace-pre-line">{service.description}</p>

                                        {/* View Mode: Layered Tags */}
                                        {serviceDisplayTags.length > 0 && (
                                            <div className="space-y-4 mb-6 pt-2 border-t border-gray-50">
                                                {/* Categories */}
                                                {(() => {
                                                    const leafIds = ALL_LEAF_CATEGORIES.map(c => c.name);
                                                    const catTags = serviceDisplayTags.filter(t => leafIds.includes(t));
                                                    if (catTags.length === 0) return null;
                                                    return (
                                                        <div>
                                                            <h5 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-0.5">业务分类</h5>
                                                            <div className="flex flex-wrap gap-2">
                                                                {catTags.map((tag, i) => (
                                                                    <span key={tag} className={`px-2 py-0.5 text-xs font-medium rounded border ${i === 0 ? 'bg-blue-600 text-white border-blue-600' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>
                                                                        {i === 0 && <span className="opacity-70 mr-1">主</span>}
                                                                        {i > 0 && <span className="opacity-60 mr-1">次</span>}
                                                                        {tag}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                                {/* Style Facets */}
                                                {(() => {
                                                    const facetNames = STYLE_FACETS.map(f => f.name) as string[];
                                                    const styleTags = serviceDisplayTags.filter(t => facetNames.includes(t));
                                                    if (styleTags.length === 0) return null;
                                                    return (
                                                        <div>
                                                            <h5 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-0.5">风格倾向</h5>
                                                            <div className="flex flex-wrap gap-2">
                                                                {styleTags.map(tag => (
                                                                    <span key={tag} className="px-2 py-0.5 text-xs font-medium rounded border bg-blue-50 text-blue-600 border-blue-100">
                                                                        {tag}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                                {/* Tools */}
                                                {(() => {
                                                    const leafIds = ALL_LEAF_CATEGORIES.map(c => c.name) as string[];
                                                    const facetNames = STYLE_FACETS.map(f => f.name) as string[];
                                                    const toolTags = serviceDisplayTags.filter(t => !leafIds.includes(t) && !facetNames.includes(t));
                                                    if (toolTags.length === 0) return null;
                                                    return (
                                                        <div>
                                                            <h5 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-0.5">工具/模型与其他标签</h5>
                                                            <div className="flex flex-wrap gap-2">
                                                                {toolTags.map(tag => (
                                                                    <span
                                                                        key={tag}
                                                                        className={`px-2 py-0.5 text-xs font-medium rounded border ${isKnownToolTag(tag) ? 'bg-purple-50 text-purple-600 border-purple-100' : 'bg-gray-100 text-gray-500 border-gray-200'}`}
                                                                    >
                                                                        {tag}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Key Metrics Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                {(service.contentDurationRange || isEditing) && (
                                    <div className={`p-4 rounded-xl border shadow-sm flex items-center gap-3 transition-all ${isEditing ? 'bg-purple-50/30 border-purple-100' : 'bg-white border-gray-100'}`}>
                                        <div className={`p-2.5 rounded-full shrink-0 ${isEditing ? 'bg-purple-100 text-purple-600' : 'w-10 h-10 bg-purple-50 text-purple-600 flex items-center justify-center'}`}>
                                            <Clock className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-purple-600 font-bold uppercase tracking-wide mb-1 opacity-80">{isEditing ? '作品时长' : '作品时长'}</p>
                                            {isEditing ? (
                                                <input
                                                    type="text"
                                                    value={formData.contentDurationRange || ''}
                                                    onChange={e => setFormField('contentDurationRange', e.target.value)}
                                                    className="w-full text-base font-bold text-gray-900 border-none bg-transparent p-0 focus:ring-0 placeholder-gray-400"
                                                    placeholder="xx - xx (秒)"
                                                />
                                            ) : (
                                                <p className="text-sm font-bold text-gray-900 mt-0.5 truncate">{service.contentDurationRange}</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className={`p-4 rounded-xl border shadow-sm flex items-center gap-3 transition-all ${isEditing ? 'bg-emerald-50/30 border-emerald-100' : 'bg-white border-gray-100'}`}>
                                    <div className={`p-2.5 rounded-full shrink-0 ${isEditing ? 'bg-emerald-100 text-emerald-600' : 'w-10 h-10 bg-emerald-50 text-emerald-600 flex items-center justify-center'}`}>
                                        <DollarSign className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-emerald-600 font-bold uppercase tracking-wide mb-1 opacity-80">{isEditing ? '价格区间' : '价格区间'}</p>
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={formData.priceRange}
                                                onChange={e => setFormField('priceRange', e.target.value)}
                                                className="w-full text-base font-bold text-gray-900 border-none bg-transparent p-0 focus:ring-0 placeholder-gray-400"
                                                placeholder="xx - xx (元)"
                                            />
                                        ) : (
                                            <p className="text-sm font-bold text-gray-900 mt-0.5 truncate">{service.priceRange}</p>
                                        )}
                                    </div>
                                </div>

                                <div className={`p-4 rounded-xl border shadow-sm flex items-center gap-3 transition-all ${isEditing ? 'bg-orange-50/30 border-orange-100' : 'bg-white border-gray-100'}`}>
                                    <div className={`p-2.5 rounded-full shrink-0 ${isEditing ? 'bg-orange-100 text-orange-600' : 'w-10 h-10 bg-orange-50 text-orange-600 flex items-center justify-center'}`}>
                                        <Calendar className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-orange-600 font-bold uppercase tracking-wide mb-1 opacity-80">{isEditing ? '预计工期' : '预计工期'}</p>
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={formData.deliveryTimeRange}
                                                onChange={e => setFormField('deliveryTimeRange', e.target.value)}
                                                className="w-full text-base font-bold text-gray-900 border-none bg-transparent p-0 focus:ring-0 placeholder-gray-400"
                                                placeholder="xx - xx (天)"
                                            />
                                        ) : (
                                            <p className="text-sm font-bold text-gray-900 mt-0.5 truncate">{service.deliveryTimeRange}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Area: Tabs & Content */}
                    <div className="flex-col min-h-0 bg-white">
                        {/* Tabs Header - Only show if playing viewer role, basically always in view mode. In edit mode we just show Works list as "Works" section? 
                           Actually for edit mode, we can just show Works. Reviews are read only.
                           Let's keep tabs but maybe disable Reviews tab in edit mode? Or just hide it.
                        */}
                        {!isEditing && (
                            <div className="px-8 border-b border-gray-100 sticky top-0 bg-white z-10">
                                <div className="flex gap-8">
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('works')}
                                        className={`py-4 text-sm font-medium border-b-2 transition-colors relative ${activeTab === 'works'
                                            ? 'border-black text-black'
                                            : 'border-transparent text-gray-500 hover:text-gray-800'
                                            }`}
                                    >
                                        示例作品 ({displayedExampleWorks.length})
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('reviews')}
                                        className={`py-4 text-sm font-medium border-b-2 transition-colors relative ${activeTab === 'reviews'
                                            ? 'border-black text-black'
                                            : 'border-transparent text-gray-500 hover:text-gray-800'
                                            }`}
                                    >
                                        客户评价 ({serviceReviews.length})
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Tab Content */}
                        <div className="p-8 bg-gray-50/30">
                            {/* In Edit Mode, we treat "Works" as the only content needed to edit */}
                            {(activeTab === 'works' || isEditing) && (
                                <div>
                                    {isEditing && (
                                        <div className="px-1 py-4 border-b border-gray-100 mb-4">
                                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                                示例作品
                                                <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                                    {formExampleWorks.length}
                                                </span>
                                            </h3>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {displayedExampleWorks.map(work => {
                                            const sourceUrl = 'sourceUrl' in work ? work.sourceUrl : undefined;
                                            const description = 'description' in work ? work.description : undefined;

                                            return (
                                            <div key={work.id} className="relative group">
                                                {sourceUrl ? (
                                                    <ExampleWorkUrlCard
                                                        type={work.type}
                                                        thumbnailUrl={work.thumbnailUrl}
                                                        videoUrl={work.videoUrl}
                                                        images={work.images}
                                                        title={work.title}
                                                        description={description}
                                                        sourceUrl={sourceUrl}
                                                        onClick={() => handleWorkClick(work)}
                                                    />
                                                ) : (
                                                    <VideoCard
                                                        id={Number(work.id)}
                                                        type={work.type}
                                                        images={work.images}
                                                        videoUrl={work.videoUrl}
                                                        thumbnailUrl={work.thumbnailUrl}
                                                        title={work.title}
                                                        likes={0}
                                                        createdAt=""
                                                        uploadState={!isEditing && 'uploadState' in work ? (work as PendingExampleWork).uploadState : undefined}
                                                        onClick={() => handleWorkClick(work)}
                                                    />
                                                )}
                                                {isEditing && (
                                                    <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleStartEditExampleWork(work);
                                                            }}
                                                            className="p-1.5 bg-black/75 text-white rounded-full hover:bg-black transition-colors"
                                                            title="编辑作品"
                                                        >
                                                            <Edit3 className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveWork(work.id);
                                                            }}
                                                            className="p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                                                            title="移除作品"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            );
                                        })}
                                        {isEditing && (
                                            <button
                                                type="button"
                                                className="h-full min-h-[200px] border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-gray-600 hover:border-gray-400 transition-all bg-gray-50 hover:bg-gray-100"
                                                onClick={() => setShowExampleWorkSourceModal(true)}
                                            >
                                                <Plus className="w-8 h-8" />
                                                <span className="font-medium text-sm">
                                                    {isLoadingAvailableWorks ? '加载作品中...' : '添加示例作品'}
                                                </span>
                                            </button>
                                        )}
                                        {!isEditing && serviceExampleWorks.length === 0 && (
                                            <div className="col-span-full py-12 text-center text-gray-400">
                                                暂无示例作品
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'reviews' && !isEditing && (
                                <div className="max-w-3xl space-y-6">
                                    {/* Review Summary Stats */}
                                    {totalReviews > 0 && (
                                        <div className="bg-white p-4 rounded-xl border border-gray-100 flex items-center gap-6 mb-6">
                                            <div className="flex items-center gap-2">
                                                <div className="p-2 bg-yellow-50 rounded-lg">
                                                    <ThumbsUp className="w-5 h-5 text-yellow-500" />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500">好评数</p>
                                                    <p className="text-sm font-bold text-gray-900">{positiveReviews}/{totalReviews}</p>
                                                </div>
                                            </div>
                                            <div className="w-[1px] h-8 bg-gray-100"></div>
                                            <div className="flex items-center gap-2">
                                                <div className="p-2 bg-yellow-50 rounded-lg">
                                                    <Star className="w-5 h-5 text-yellow-500" />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500">评分</p>
                                                    <p className="text-sm font-bold text-gray-900">{averageRating}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {service.reviews.map(review => (
                                        <div key={review.id} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center gap-3">
                                                    <img src={review.avatar} alt={review.username} className="w-10 h-10 rounded-full object-cover" />
                                                    <div>
                                                        <h4 className="font-medium text-gray-900 text-sm">{review.username}</h4>
                                                        <div className="flex items-center gap-0.5 mt-0.5">
                                                            {[...Array(5)].map((_, i) => (
                                                                <Star
                                                                    key={i}
                                                                    className={`w-3 h-3 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                                <span className="text-gray-400 text-xs">{review.createdAt}</span>
                                            </div>
                                            <p className="text-gray-600 text-sm leading-relaxed mb-4">
                                                {review.content}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer Buttons */}
                {isEditing && (
                    <div className="flex justify-end gap-3 pt-6 pb-6 pr-6 border-t border-gray-100 mt-auto bg-white">
                        <button
                            type="button"
                            onClick={() => void handleRequestClose()}
                            className="px-6 py-2 rounded-full border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                        >
                            取消
                        </button>
                        <button
                            type="button"
                            onClick={(e) => handleSave('draft', e)}
                            disabled={!formData.title || isSubmitting || !hasUnsavedChanges}
                            className={`px-8 py-2 rounded-full font-medium transition-all ${!formData.title || isSubmitting || !hasUnsavedChanges
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            {submitMode === 'draft' ? '保存中...' : '保存'}
                        </button>
                        <button
                            type="button"
                            onClick={(e) => handleSave('publish', e)}
                            disabled={!formData.title || isSubmitting}
                            className={`px-8 py-2 rounded-full font-medium transition-all ${!formData.title || isSubmitting
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-black text-white hover:bg-gray-800'
                                }`}
                        >
                            {submitMode === 'publish' ? '发布中...' : '发布'}
                        </button>
                    </div>
                )}
            </form>

            {/* Sub Modals */}
            <ExampleWorkSourceModal
                isOpen={showExampleWorkSourceModal}
                onClose={() => setShowExampleWorkSourceModal(false)}
                onSelect={handleSelectExampleWorkSource}
                canSelectMyWorks={!!currentUser.id && currentUser.id !== '0'}
                zIndex={70}
            />

            <SimpleUploadModal
                isOpen={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                onSave={handlePublishWork}
                zIndex={70}
            />

            <WorkSelectorModal
                isOpen={showWorkSelectorModal}
                onClose={() => setShowWorkSelectorModal(false)}
                onSelect={handleSelectCreatorWorks}
                works={availableWorks}
                isLoading={isLoadingAvailableWorks}
                selectedWorkIds={formData.exampleWorks.map((work) => work.id)}
                zIndex={75}
            />

            <ExampleWorkUrlModal
                isOpen={showUrlModal}
                onClose={() => setShowUrlModal(false)}
                onImport={handleImportUrlWork}
                zIndex={80}
            />

            <ExampleWorkEditModal
                isOpen={!!editingExampleWork}
                work={editingExampleWork}
                onClose={() => setEditingExampleWork(null)}
                onSave={handleSaveEditedExampleWork}
                zIndex={85}
            />

            <ExampleWorkViewModal
                isOpen={!!selectedUrlWork}
                work={selectedUrlWork}
                onClose={() => setSelectedUrlWork(null)}
                zIndex={90}
            />

            {selectedWork && (
                <WorkDetailModal
                    work={selectedWork}
                    isOpen={!!selectedWork}
                    onClose={() => setSelectedWork(null)}
                    isOwnProfile={isOwnProfile}
                    enablePostInteractions={false}
                />
            )}
        </ProfileEditModal>
    );
};

export default ServiceDetailModal;
